import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import styles from './Settings.module.css';

const PLANS = [
  {
    tier: 'SOLO',
    label: 'Solo',
    price: '$49/mo',
    features: ['1 business', 'Up to 3 reporters', 'Daily entries & audit trail', 'Amendments & notifications'],
  },
  {
    tier: 'MULTI',
    label: 'Multi',
    price: '$99/mo',
    features: ['Up to 5 businesses', 'Up to 15 reporters', 'Side-by-side comparison', 'All Solo features'],
  },
  {
    tier: 'ENTERPRISE',
    label: 'Enterprise',
    price: '$249/mo',
    features: ['Unlimited businesses', 'Unlimited reporters', 'Priority support', 'All Multi features'],
  },
];

const TIER_RANK = { SOLO: 0, MULTI: 1, ENTERPRISE: 2 };

// All IANA timezones — loaded from browser's Intl API
function getAllTimezones() {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    // Fallback for older browsers
    return [
      'Africa/Abidjan','Africa/Accra','Africa/Addis_Ababa','Africa/Algiers','Africa/Asmara',
      'Africa/Bamako','Africa/Bangui','Africa/Banjul','Africa/Bissau','Africa/Blantyre',
      'Africa/Brazzaville','Africa/Bujumbura','Africa/Cairo','Africa/Casablanca','Africa/Ceuta',
      'Africa/Conakry','Africa/Dakar','Africa/Dar_es_Salaam','Africa/Djibouti','Africa/Douala',
      'Africa/El_Aaiun','Africa/Freetown','Africa/Gaborone','Africa/Harare','Africa/Johannesburg',
      'Africa/Juba','Africa/Kampala','Africa/Khartoum','Africa/Kigali','Africa/Kinshasa',
      'Africa/Lagos','Africa/Libreville','Africa/Lome','Africa/Luanda','Africa/Lubumbashi',
      'Africa/Lusaka','Africa/Malabo','Africa/Maputo','Africa/Maseru','Africa/Mbabane',
      'Africa/Mogadishu','Africa/Monrovia','Africa/Nairobi','Africa/Ndjamena','Africa/Niamey',
      'Africa/Nouakchott','Africa/Ouagadougou','Africa/Porto-Novo','Africa/Sao_Tome','Africa/Tripoli',
      'Africa/Tunis','Africa/Windhoek',
      'America/Adak','America/Anchorage','America/Anguilla','America/Antigua','America/Araguaina',
      'America/Argentina/Buenos_Aires','America/Argentina/Catamarca','America/Argentina/Cordoba',
      'America/Argentina/Jujuy','America/Argentina/La_Rioja','America/Argentina/Mendoza',
      'America/Argentina/Rio_Gallegos','America/Argentina/Salta','America/Argentina/San_Juan',
      'America/Argentina/San_Luis','America/Argentina/Tucuman','America/Argentina/Ushuaia',
      'America/Aruba','America/Asuncion','America/Atikokan','America/Bahia','America/Bahia_Banderas',
      'America/Barbados','America/Belem','America/Belize','America/Blanc-Sablon','America/Boa_Vista',
      'America/Bogota','America/Boise','America/Cambridge_Bay','America/Campo_Grande','America/Cancun',
      'America/Caracas','America/Cayenne','America/Cayman','America/Chicago','America/Chihuahua',
      'America/Costa_Rica','America/Creston','America/Cuiaba','America/Curacao','America/Danmarkshavn',
      'America/Dawson','America/Dawson_Creek','America/Denver','America/Detroit','America/Dominica',
      'America/Edmonton','America/Eirunepe','America/El_Salvador','America/Fort_Nelson','America/Fortaleza',
      'America/Glace_Bay','America/Godthab','America/Goose_Bay','America/Grand_Turk','America/Grenada',
      'America/Guadeloupe','America/Guatemala','America/Guayaquil','America/Guyana','America/Halifax',
      'America/Havana','America/Hermosillo','America/Indiana/Indianapolis','America/Indiana/Knox',
      'America/Indiana/Marengo','America/Indiana/Petersburg','America/Indiana/Tell_City',
      'America/Indiana/Vevay','America/Indiana/Vincennes','America/Indiana/Winamac','America/Inuvik',
      'America/Iqaluit','America/Jamaica','America/Juneau','America/Kentucky/Louisville',
      'America/Kentucky/Monticello','America/Kralendijk','America/La_Paz','America/Lima',
      'America/Los_Angeles','America/Lower_Princes','America/Maceio','America/Managua',
      'America/Manaus','America/Marigot','America/Martinique','America/Matamoros','America/Mazatlan',
      'America/Menominee','America/Merida','America/Metlakatla','America/Mexico_City','America/Miquelon',
      'America/Moncton','America/Monterrey','America/Montevideo','America/Montserrat','America/Nassau',
      'America/New_York','America/Nipigon','America/Nome','America/Noronha','America/North_Dakota/Beulah',
      'America/North_Dakota/Center','America/North_Dakota/New_Salem','America/Nuuk','America/Ojinaga',
      'America/Panama','America/Pangnirtung','America/Paramaribo','America/Phoenix','America/Port-au-Prince',
      'America/Port_of_Spain','America/Porto_Velho','America/Puerto_Rico','America/Punta_Arenas',
      'America/Rainy_River','America/Rankin_Inlet','America/Recife','America/Regina','America/Resolute',
      'America/Rio_Branco','America/Santarem','America/Santiago','America/Santo_Domingo','America/Sao_Paulo',
      'America/Scoresbysund','America/Sitka','America/St_Barthelemy','America/St_Johns','America/St_Kitts',
      'America/St_Lucia','America/St_Thomas','America/St_Vincent','America/Swift_Current',
      'America/Tegucigalpa','America/Thule','America/Thunder_Bay','America/Tijuana','America/Toronto',
      'America/Tortola','America/Vancouver','America/Whitehorse','America/Winnipeg','America/Yakutat',
      'America/Yellowknife',
      'Antarctica/Casey','Antarctica/Davis','Antarctica/DumontDUrville','Antarctica/Macquarie',
      'Antarctica/Mawson','Antarctica/McMurdo','Antarctica/Palmer','Antarctica/Rothera',
      'Antarctica/Syowa','Antarctica/Troll','Antarctica/Vostok',
      'Asia/Aden','Asia/Almaty','Asia/Amman','Asia/Anadyr','Asia/Aqtau','Asia/Aqtobe','Asia/Ashgabat',
      'Asia/Atyrau','Asia/Baghdad','Asia/Bahrain','Asia/Baku','Asia/Bangkok','Asia/Barnaul',
      'Asia/Beirut','Asia/Bishkek','Asia/Brunei','Asia/Chita','Asia/Choibalsan','Asia/Colombo',
      'Asia/Damascus','Asia/Dhaka','Asia/Dili','Asia/Dubai','Asia/Dushanbe','Asia/Famagusta',
      'Asia/Gaza','Asia/Hebron','Asia/Ho_Chi_Minh','Asia/Hong_Kong','Asia/Hovd','Asia/Irkutsk',
      'Asia/Jakarta','Asia/Jayapura','Asia/Jerusalem','Asia/Kabul','Asia/Kamchatka','Asia/Karachi',
      'Asia/Kathmandu','Asia/Khandyga','Asia/Kolkata','Asia/Krasnoyarsk','Asia/Kuala_Lumpur',
      'Asia/Kuching','Asia/Kuwait','Asia/Macau','Asia/Magadan','Asia/Makassar','Asia/Manila',
      'Asia/Muscat','Asia/Nicosia','Asia/Novokuznetsk','Asia/Novosibirsk','Asia/Omsk','Asia/Oral',
      'Asia/Phnom_Penh','Asia/Pontianak','Asia/Pyongyang','Asia/Qatar','Asia/Qostanay','Asia/Qyzylorda',
      'Asia/Riyadh','Asia/Sakhalin','Asia/Samarkand','Asia/Seoul','Asia/Shanghai','Asia/Singapore',
      'Asia/Srednekolymsk','Asia/Taipei','Asia/Tashkent','Asia/Tbilisi','Asia/Tehran','Asia/Thimphu',
      'Asia/Tokyo','Asia/Tomsk','Asia/Ulaanbaatar','Asia/Urumqi','Asia/Ust-Nera','Asia/Vientiane',
      'Asia/Vladivostok','Asia/Yakutsk','Asia/Yangon','Asia/Yekaterinburg','Asia/Yerevan',
      'Atlantic/Azores','Atlantic/Bermuda','Atlantic/Canary','Atlantic/Cape_Verde','Atlantic/Faroe',
      'Atlantic/Madeira','Atlantic/Reykjavik','Atlantic/South_Georgia','Atlantic/St_Helena',
      'Atlantic/Stanley',
      'Australia/Adelaide','Australia/Brisbane','Australia/Broken_Hill','Australia/Darwin',
      'Australia/Eucla','Australia/Hobart','Australia/Lindeman','Australia/Lord_Howe',
      'Australia/Melbourne','Australia/Perth','Australia/Sydney',
      'Europe/Amsterdam','Europe/Andorra','Europe/Astrakhan','Europe/Athens','Europe/Belgrade',
      'Europe/Berlin','Europe/Bratislava','Europe/Brussels','Europe/Bucharest','Europe/Budapest',
      'Europe/Busingen','Europe/Chisinau','Europe/Copenhagen','Europe/Dublin','Europe/Gibraltar',
      'Europe/Guernsey','Europe/Helsinki','Europe/Isle_of_Man','Europe/Istanbul','Europe/Jersey',
      'Europe/Kaliningrad','Europe/Kiev','Europe/Kirov','Europe/Lisbon','Europe/Ljubljana',
      'Europe/London','Europe/Luxembourg','Europe/Madrid','Europe/Malta','Europe/Mariehamn',
      'Europe/Minsk','Europe/Monaco','Europe/Moscow','Europe/Nicosia','Europe/Oslo','Europe/Paris',
      'Europe/Podgorica','Europe/Prague','Europe/Riga','Europe/Rome','Europe/Samara','Europe/San_Marino',
      'Europe/Sarajevo','Europe/Saratov','Europe/Simferopol','Europe/Skopje','Europe/Sofia',
      'Europe/Stockholm','Europe/Tallinn','Europe/Tirane','Europe/Ulyanovsk','Europe/Uzhgorod',
      'Europe/Vaduz','Europe/Vatican','Europe/Vienna','Europe/Vilnius','Europe/Volgograd',
      'Europe/Warsaw','Europe/Zagreb','Europe/Zaporozhye','Europe/Zurich',
      'Indian/Antananarivo','Indian/Chagos','Indian/Christmas','Indian/Cocos','Indian/Comoro',
      'Indian/Kerguelen','Indian/Mahe','Indian/Maldives','Indian/Mauritius','Indian/Mayotte',
      'Indian/Reunion',
      'Pacific/Apia','Pacific/Auckland','Pacific/Bougainville','Pacific/Chatham','Pacific/Chuuk',
      'Pacific/Easter','Pacific/Efate','Pacific/Enderbury','Pacific/Fakaofo','Pacific/Fiji',
      'Pacific/Funafuti','Pacific/Galapagos','Pacific/Gambier','Pacific/Guadalcanal','Pacific/Guam',
      'Pacific/Honolulu','Pacific/Kiritimati','Pacific/Kosrae','Pacific/Kwajalein','Pacific/Majuro',
      'Pacific/Marquesas','Pacific/Midway','Pacific/Nauru','Pacific/Niue','Pacific/Norfolk',
      'Pacific/Noumea','Pacific/Pago_Pago','Pacific/Palau','Pacific/Pitcairn','Pacific/Pohnpei',
      'Pacific/Port_Moresby','Pacific/Rarotonga','Pacific/Saipan','Pacific/Tahiti','Pacific/Tarawa',
      'Pacific/Tongatapu','Pacific/Wake','Pacific/Wallis',
      'UTC',
    ];
  }
}

const ALL_TIMEZONES = getAllTimezones();

// Full ISO 4217 currency list
const CURRENCIES = [
  { code: 'AED', label: 'AED — UAE Dirham' },
  { code: 'AFN', label: 'AFN — Afghan Afghani' },
  { code: 'ALL', label: 'ALL — Albanian Lek' },
  { code: 'AMD', label: 'AMD — Armenian Dram' },
  { code: 'ANG', label: 'ANG — Netherlands Antillean Guilder' },
  { code: 'AOA', label: 'AOA — Angolan Kwanza' },
  { code: 'ARS', label: 'ARS — Argentine Peso' },
  { code: 'AUD', label: 'AUD — Australian Dollar' },
  { code: 'AWG', label: 'AWG — Aruban Florin' },
  { code: 'AZN', label: 'AZN — Azerbaijani Manat' },
  { code: 'BAM', label: 'BAM — Bosnia-Herzegovina Convertible Mark' },
  { code: 'BBD', label: 'BBD — Barbadian Dollar' },
  { code: 'BDT', label: 'BDT — Bangladeshi Taka' },
  { code: 'BGN', label: 'BGN — Bulgarian Lev' },
  { code: 'BHD', label: 'BHD — Bahraini Dinar' },
  { code: 'BIF', label: 'BIF — Burundian Franc' },
  { code: 'BMD', label: 'BMD — Bermudian Dollar' },
  { code: 'BND', label: 'BND — Brunei Dollar' },
  { code: 'BOB', label: 'BOB — Bolivian Boliviano' },
  { code: 'BRL', label: 'BRL — Brazilian Real' },
  { code: 'BSD', label: 'BSD — Bahamian Dollar' },
  { code: 'BTN', label: 'BTN — Bhutanese Ngultrum' },
  { code: 'BWP', label: 'BWP — Botswana Pula' },
  { code: 'BYN', label: 'BYN — Belarusian Ruble' },
  { code: 'BZD', label: 'BZD — Belize Dollar' },
  { code: 'CAD', label: 'CAD — Canadian Dollar' },
  { code: 'CDF', label: 'CDF — Congolese Franc' },
  { code: 'CHF', label: 'CHF — Swiss Franc' },
  { code: 'CLP', label: 'CLP — Chilean Peso' },
  { code: 'CNY', label: 'CNY — Chinese Yuan' },
  { code: 'COP', label: 'COP — Colombian Peso' },
  { code: 'CRC', label: 'CRC — Costa Rican Colón' },
  { code: 'CUP', label: 'CUP — Cuban Peso' },
  { code: 'CVE', label: 'CVE — Cape Verdean Escudo' },
  { code: 'CZK', label: 'CZK — Czech Koruna' },
  { code: 'DJF', label: 'DJF — Djiboutian Franc' },
  { code: 'DKK', label: 'DKK — Danish Krone' },
  { code: 'DOP', label: 'DOP — Dominican Peso' },
  { code: 'DZD', label: 'DZD — Algerian Dinar' },
  { code: 'EGP', label: 'EGP — Egyptian Pound' },
  { code: 'ERN', label: 'ERN — Eritrean Nakfa' },
  { code: 'ETB', label: 'ETB — Ethiopian Birr' },
  { code: 'EUR', label: 'EUR — Euro' },
  { code: 'FJD', label: 'FJD — Fijian Dollar' },
  { code: 'FKP', label: 'FKP — Falkland Islands Pound' },
  { code: 'GBP', label: 'GBP — British Pound Sterling' },
  { code: 'GEL', label: 'GEL — Georgian Lari' },
  { code: 'GHS', label: 'GHS — Ghanaian Cedi' },
  { code: 'GIP', label: 'GIP — Gibraltar Pound' },
  { code: 'GMD', label: 'GMD — Gambian Dalasi' },
  { code: 'GNF', label: 'GNF — Guinean Franc' },
  { code: 'GTQ', label: 'GTQ — Guatemalan Quetzal' },
  { code: 'GYD', label: 'GYD — Guyanese Dollar' },
  { code: 'HKD', label: 'HKD — Hong Kong Dollar' },
  { code: 'HNL', label: 'HNL — Honduran Lempira' },
  { code: 'HTG', label: 'HTG — Haitian Gourde' },
  { code: 'HUF', label: 'HUF — Hungarian Forint' },
  { code: 'IDR', label: 'IDR — Indonesian Rupiah' },
  { code: 'ILS', label: 'ILS — Israeli New Shekel' },
  { code: 'INR', label: 'INR — Indian Rupee' },
  { code: 'IQD', label: 'IQD — Iraqi Dinar' },
  { code: 'IRR', label: 'IRR — Iranian Rial' },
  { code: 'ISK', label: 'ISK — Icelandic Króna' },
  { code: 'JMD', label: 'JMD — Jamaican Dollar' },
  { code: 'JOD', label: 'JOD — Jordanian Dinar' },
  { code: 'JPY', label: 'JPY — Japanese Yen' },
  { code: 'KES', label: 'KES — Kenyan Shilling' },
  { code: 'KGS', label: 'KGS — Kyrgyzstani Som' },
  { code: 'KHR', label: 'KHR — Cambodian Riel' },
  { code: 'KMF', label: 'KMF — Comorian Franc' },
  { code: 'KRW', label: 'KRW — South Korean Won' },
  { code: 'KWD', label: 'KWD — Kuwaiti Dinar' },
  { code: 'KYD', label: 'KYD — Cayman Islands Dollar' },
  { code: 'KZT', label: 'KZT — Kazakhstani Tenge' },
  { code: 'LAK', label: 'LAK — Laotian Kip' },
  { code: 'LBP', label: 'LBP — Lebanese Pound' },
  { code: 'LKR', label: 'LKR — Sri Lankan Rupee' },
  { code: 'LRD', label: 'LRD — Liberian Dollar' },
  { code: 'LSL', label: 'LSL — Lesotho Loti' },
  { code: 'LYD', label: 'LYD — Libyan Dinar' },
  { code: 'MAD', label: 'MAD — Moroccan Dirham' },
  { code: 'MDL', label: 'MDL — Moldovan Leu' },
  { code: 'MGA', label: 'MGA — Malagasy Ariary' },
  { code: 'MKD', label: 'MKD — Macedonian Denar' },
  { code: 'MMK', label: 'MMK — Myanmar Kyat' },
  { code: 'MNT', label: 'MNT — Mongolian Tögrög' },
  { code: 'MOP', label: 'MOP — Macanese Pataca' },
  { code: 'MRU', label: 'MRU — Mauritanian Ouguiya' },
  { code: 'MUR', label: 'MUR — Mauritian Rupee' },
  { code: 'MVR', label: 'MVR — Maldivian Rufiyaa' },
  { code: 'MWK', label: 'MWK — Malawian Kwacha' },
  { code: 'MXN', label: 'MXN — Mexican Peso' },
  { code: 'MYR', label: 'MYR — Malaysian Ringgit' },
  { code: 'MZN', label: 'MZN — Mozambican Metical' },
  { code: 'NAD', label: 'NAD — Namibian Dollar' },
  { code: 'NGN', label: 'NGN — Nigerian Naira' },
  { code: 'NIO', label: 'NIO — Nicaraguan Córdoba' },
  { code: 'NOK', label: 'NOK — Norwegian Krone' },
  { code: 'NPR', label: 'NPR — Nepalese Rupee' },
  { code: 'NZD', label: 'NZD — New Zealand Dollar' },
  { code: 'OMR', label: 'OMR — Omani Rial' },
  { code: 'PAB', label: 'PAB — Panamanian Balboa' },
  { code: 'PEN', label: 'PEN — Peruvian Sol' },
  { code: 'PGK', label: 'PGK — Papua New Guinean Kina' },
  { code: 'PHP', label: 'PHP — Philippine Peso' },
  { code: 'PKR', label: 'PKR — Pakistani Rupee' },
  { code: 'PLN', label: 'PLN — Polish Złoty' },
  { code: 'PYG', label: 'PYG — Paraguayan Guaraní' },
  { code: 'QAR', label: 'QAR — Qatari Riyal' },
  { code: 'RON', label: 'RON — Romanian Leu' },
  { code: 'RSD', label: 'RSD — Serbian Dinar' },
  { code: 'RUB', label: 'RUB — Russian Ruble' },
  { code: 'RWF', label: 'RWF — Rwandan Franc' },
  { code: 'SAR', label: 'SAR — Saudi Riyal' },
  { code: 'SBD', label: 'SBD — Solomon Islands Dollar' },
  { code: 'SCR', label: 'SCR — Seychellois Rupee' },
  { code: 'SDG', label: 'SDG — Sudanese Pound' },
  { code: 'SEK', label: 'SEK — Swedish Krona' },
  { code: 'SGD', label: 'SGD — Singapore Dollar' },
  { code: 'SHP', label: 'SHP — Saint Helena Pound' },
  { code: 'SLE', label: 'SLE — Sierra Leonean Leone' },
  { code: 'SOS', label: 'SOS — Somali Shilling' },
  { code: 'SRD', label: 'SRD — Surinamese Dollar' },
  { code: 'STN', label: 'STN — São Tomé & Príncipe Dobra' },
  { code: 'SYP', label: 'SYP — Syrian Pound' },
  { code: 'SZL', label: 'SZL — Swazi Lilangeni' },
  { code: 'THB', label: 'THB — Thai Baht' },
  { code: 'TJS', label: 'TJS — Tajikistani Somoni' },
  { code: 'TMT', label: 'TMT — Turkmenistani Manat' },
  { code: 'TND', label: 'TND — Tunisian Dinar' },
  { code: 'TOP', label: 'TOP — Tongan Paʻanga' },
  { code: 'TRY', label: 'TRY — Turkish Lira' },
  { code: 'TTD', label: 'TTD — Trinidad & Tobago Dollar' },
  { code: 'TWD', label: 'TWD — New Taiwan Dollar' },
  { code: 'TZS', label: 'TZS — Tanzanian Shilling' },
  { code: 'UAH', label: 'UAH — Ukrainian Hryvnia' },
  { code: 'UGX', label: 'UGX — Ugandan Shilling' },
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'UYU', label: 'UYU — Uruguayan Peso' },
  { code: 'UZS', label: 'UZS — Uzbekistani Som' },
  { code: 'VES', label: 'VES — Venezuelan Bolívar' },
  { code: 'VND', label: 'VND — Vietnamese Đồng' },
  { code: 'VUV', label: 'VUV — Vanuatu Vatu' },
  { code: 'WST', label: 'WST — Samoan Tālā' },
  { code: 'XAF', label: 'XAF — Central African CFA Franc' },
  { code: 'XCD', label: 'XCD — East Caribbean Dollar' },
  { code: 'XOF', label: 'XOF — West African CFA Franc' },
  { code: 'XPF', label: 'XPF — CFP Franc' },
  { code: 'YER', label: 'YER — Yemeni Rial' },
  { code: 'ZAR', label: 'ZAR — South African Rand' },
  { code: 'ZMW', label: 'ZMW — Zambian Kwacha' },
  { code: 'ZWL', label: 'ZWL — Zimbabwean Dollar' },
];

// ── Searchable select ─────────────────────────────────────────────────────────

function SearchableSelect({ value, onChange, options, placeholder = 'Search...' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const q = search.toLowerCase();
  const filtered = options.filter(opt => {
    const label = typeof opt === 'string' ? opt : opt.label;
    const val = typeof opt === 'string' ? opt : (opt.code || opt.value);
    return label.toLowerCase().includes(q) || val.toLowerCase().includes(q);
  });

  const selectedLabel = (() => {
    const match = options.find(opt =>
      (typeof opt === 'string' ? opt : (opt.code || opt.value)) === value
    );
    if (!match) return value;
    return typeof match === 'string' ? match : match.label;
  })();

  function select(opt) {
    const val = typeof opt === 'string' ? opt : (opt.code || opt.value);
    onChange(val);
    setOpen(false);
    setSearch('');
  }

  return (
    <div ref={containerRef} className={styles.searchSelect}>
      <button
        type="button"
        className={styles.searchSelectTrigger}
        onClick={() => setOpen(o => !o)}
      >
        <span className={styles.searchSelectValue}>{selectedLabel || placeholder}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className={styles.searchSelectDropdown}>
          <div className={styles.searchSelectSearch}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={placeholder}
              className={styles.searchSelectInput}
            />
          </div>
          <div className={styles.searchSelectOptions}>
            {filtered.length === 0 ? (
              <div className={styles.searchSelectEmpty}>No results</div>
            ) : (
              filtered.map(opt => {
                const val = typeof opt === 'string' ? opt : (opt.code || opt.value);
                const label = typeof opt === 'string' ? opt : opt.label;
                const isSelected = val === value;
                return (
                  <div
                    key={val}
                    className={`${styles.searchSelectOption} ${isSelected ? styles.searchSelectOptionSelected : ''}`}
                    onMouseDown={e => { e.preventDefault(); select(opt); }}
                  >
                    {label}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Profile card ─────────────────────────────────────────────────────────────

function ProfileSection({ user, onEdit }) {
  const tier = user?.profile?.subscription_tier || 'SOLO';
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>Profile</h2>
        <button onClick={onEdit} className={styles.btnIcon} title="Edit profile">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
      </div>
      <div className={styles.profileGrid}>
        <div className={styles.profileField}>
          <span className={styles.fieldLabel}>Email</span>
          <span className={styles.fieldValue}>{user?.email}</span>
        </div>
        <div className={styles.profileField}>
          <span className={styles.fieldLabel}>Name</span>
          <span className={styles.fieldValue}>
            {[user?.first_name, user?.last_name].filter(Boolean).join(' ') || '—'}
          </span>
        </div>
        <div className={styles.profileField}>
          <span className={styles.fieldLabel}>Role</span>
          <span className={styles.fieldValue}>{user?.role}</span>
        </div>
        <div className={styles.profileField}>
          <span className={styles.fieldLabel}>Plan</span>
          <span className={styles.fieldValue}>{tier}</span>
        </div>
        <div className={styles.profileField}>
          <span className={styles.fieldLabel}>Timezone</span>
          <span className={styles.fieldValue}>{user?.profile?.timezone || '—'}</span>
        </div>
        <div className={styles.profileField}>
          <span className={styles.fieldLabel}>Currency</span>
          <span className={styles.fieldValue}>{user?.profile?.currency || '—'}</span>
        </div>
      </div>
    </div>
  );
}

// ── Edit profile modal ────────────────────────────────────────────────────────

function EditProfileModal({ user, onClose, onSaved }) {
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [timezone, setTimezone] = useState(user?.profile?.timezone || 'Australia/Sydney');
  const [currency, setCurrency] = useState(user?.profile?.currency || 'AUD');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [upgrading, setUpgrading] = useState('');

  const currentTier = user?.profile?.subscription_tier || 'SOLO';
  const currentRank = TIER_RANK[currentTier] ?? 0;

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.patch('/auth/me/', { first_name: firstName, last_name: lastName, timezone, currency });
      setSuccess('Profile updated successfully.');
      await onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpgrade(tier) {
    setUpgrading(tier);
    setError('');
    try {
      const base = window.location.origin;
      const res = await api.post('/subscriptions/checkout/', {
        tier,
        success_url: `${base}/settings?upgrade=success`,
        cancel_url: `${base}/settings?upgrade=cancelled`,
      });
      if (res.data.success) {
        window.location.href = res.data.data.checkout_url;
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start upgrade. Please try again.');
      setUpgrading('');
    }
  }

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Edit Profile</h2>
          <button onClick={onClose} className={styles.btnClose} aria-label="Close">×</button>
        </div>

        <div className={styles.modalBody}>
          {error && <div className={styles.error}>{error}</div>}
          {success && <div className={styles.success}>{success}</div>}

          {/* Profile fields */}
          <form onSubmit={handleSave}>
            <div className={styles.formRow}>
              <label className={styles.label}>
                First Name
                <input
                  type="text"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  className={styles.input}
                />
              </label>
              <label className={styles.label}>
                Last Name
                <input
                  type="text"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  className={styles.input}
                />
              </label>
            </div>

            <div className={styles.formRow}>
              <label className={styles.label}>
                Timezone
                <SearchableSelect
                  value={timezone}
                  onChange={setTimezone}
                  options={ALL_TIMEZONES}
                  placeholder="Search timezones..."
                />
              </label>
              <label className={styles.label}>
                Currency
                <SearchableSelect
                  value={currency}
                  onChange={setCurrency}
                  options={CURRENCIES}
                  placeholder="Search currencies..."
                />
              </label>
            </div>

            <p className={styles.emailNote}>Email cannot be changed. Contact support if needed.</p>

            <div className={styles.modalActions}>
              <button type="button" onClick={onClose} className={styles.btnSecondary}>Cancel</button>
              <button type="submit" disabled={saving} className={styles.btnPrimary}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>

          {/* Plan upgrade */}
          <div className={styles.planDivider} />
          <h3 className={styles.planSectionTitle}>Subscription Plan</h3>
          <p className={styles.planSectionNote}>
            Features of the upgraded plan are enabled only after successful payment.
          </p>
          <div className={styles.planCards}>
            {PLANS.map(plan => {
              const planRank = TIER_RANK[plan.tier] ?? 0;
              const isCurrent = plan.tier === currentTier;
              const isUpgrade = planRank > currentRank;
              return (
                <div
                  key={plan.tier}
                  className={`${styles.planCard} ${isCurrent ? styles.planCurrent : ''}`}
                >
                  <div className={styles.planCardTop}>
                    <span className={styles.planLabel}>{plan.label}</span>
                    {isCurrent && <span className={styles.currentBadge}>Current</span>}
                  </div>
                  <div className={styles.planPrice}>{plan.price}</div>
                  <ul className={styles.planFeatures}>
                    {plan.features.map(f => (
                      <li key={f} className={styles.planFeature}>{f}</li>
                    ))}
                  </ul>
                  {isUpgrade && (
                    <button
                      type="button"
                      onClick={() => handleUpgrade(plan.tier)}
                      disabled={!!upgrading}
                      className={styles.btnUpgrade}
                    >
                      {upgrading === plan.tier ? 'Redirecting...' : 'Upgrade'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 2FA card ──────────────────────────────────────────────────────────────────

function TwoFactorSection({ user }) {
  const [step, setStep] = useState('idle'); // idle, setup, verify, complete
  const [secret, setSecret] = useState('');
  const [provisioningUri, setProvisioningUri] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSetup() {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/auth/2fa/setup/');
      if (res.data.success) {
        setSecret(res.data.data.secret);
        setProvisioningUri(res.data.data.provisioning_uri);
        setStep('setup');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set up 2FA.');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/auth/2fa/verify/', { code });
      if (res.data.success) {
        setBackupCodes(res.data.data.backup_codes || []);
        setStep('complete');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code.');
    } finally {
      setLoading(false);
    }
  }

  const isEnabled = user?.two_factor_enabled;

  return (
    <div className={styles.card}>
      <h2 className={styles.cardTitle}>Two-Factor Authentication</h2>
      {isEnabled ? (
        <div className={styles.twoFaStatus}>
          <span className={styles.enabledBadge}>Enabled</span>
          <p className={styles.twoFaText}>2FA is active on your account. You'll need your authenticator app to sign in.</p>
        </div>
      ) : step === 'idle' ? (
        <div>
          <p className={styles.twoFaText}>
            Two-factor authentication adds an extra layer of security. It is mandatory for all owners.
          </p>
          {error && <div className={styles.error}>{error}</div>}
          <button onClick={handleSetup} disabled={loading} className={styles.btnPrimary}>
            {loading ? 'Setting up...' : 'Set Up 2FA'}
          </button>
        </div>
      ) : step === 'setup' ? (
        <div>
          <p className={styles.twoFaText}>
            Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.) or enter the secret manually.
          </p>
          <div className={styles.secretBox}>
            <code className={styles.secretCode}>{secret}</code>
          </div>
          <p className={styles.twoFaText} style={{ fontSize: 12, color: '#9ca3af' }}>
            URI: {provisioningUri}
          </p>
          {error && <div className={styles.error}>{error}</div>}
          <form onSubmit={handleVerify} className={styles.verifyForm}>
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value)}
              className={styles.input}
              placeholder="Enter 6-digit code"
              maxLength={6}
              required
            />
            <button type="submit" disabled={loading} className={styles.btnPrimary}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        </div>
      ) : (
        <div>
          <p className={styles.twoFaText}>2FA is now enabled! Save these backup codes securely. Each code can only be used once.</p>
          <div className={styles.backupCodes}>
            {backupCodes.map((c, i) => (
              <code key={i} className={styles.backupCode}>{c}</code>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Change password card ──────────────────────────────────────────────────────

function ChangePasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/auth/change-password/', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: newPassword,
      });
      setSuccess('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.card}>
      <h2 className={styles.cardTitle}>Change Password</h2>
      <form onSubmit={handleSubmit} className={styles.form}>
        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>{success}</div>}
        <label className={styles.label}>
          Current Password
          <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className={styles.input} required />
        </label>
        <label className={styles.label}>
          New Password
          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className={styles.input} required minLength={8} />
        </label>
        <label className={styles.label}>
          Confirm New Password
          <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={styles.input} required />
        </label>
        <div className={styles.formActions}>
          <button type="submit" disabled={saving} className={styles.btnPrimary}>
            {saving ? 'Changing...' : 'Change Password'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Notifications card ────────────────────────────────────────────────────────

function NotificationsSection() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      try {
        const res = await api.get('/notifications/');
        if (res.data.success) setNotifications(res.data.data?.notifications || []);
      } catch { /* empty */ }
      setLoading(false);
    }
    fetch();
  }, []);

  async function markRead(id) {
    try {
      await api.put(`/notifications/${id}/read/`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch { /* empty */ }
  }

  async function markAllRead() {
    try {
      await api.put('/notifications/read-all/');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch { /* empty */ }
  }

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className={styles.card}>
      <div className={styles.notifHeader}>
        <h2 className={styles.cardTitle}>
          Notifications {unreadCount > 0 && <span className={styles.unreadCount}>{unreadCount}</span>}
        </h2>
        {unreadCount > 0 && (
          <button onClick={markAllRead} className={styles.btnSmall}>Mark all read</button>
        )}
      </div>
      <p className={styles.notifDesc}>
        Amendment alerts appear here whenever a reporter's entry is amended.
      </p>
      {loading ? (
        <p className={styles.emptyText}>Loading...</p>
      ) : notifications.length === 0 ? (
        <p className={styles.emptyText}>No notifications yet.</p>
      ) : (
        <div className={styles.notifList}>
          {notifications.slice(0, 20).map(n => (
            <div key={n.id} className={`${styles.notifItem} ${!n.is_read ? styles.unread : ''}`}>
              <div className={styles.notifContent}>
                <span className={styles.notifType}>{n.notification_type}</span>
                <span className={styles.notifTitle}>{n.title}</span>
                <span className={styles.notifMessage}>{n.message}</span>
              </div>
              {!n.is_read && (
                <button onClick={() => markRead(n.id)} className={styles.markReadBtn}>
                  Mark read
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function Settings() {
  const { user, fetchUser } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);

  useEffect(() => {
    const upgrade = searchParams.get('upgrade');
    if (upgrade === 'success') {
      fetchUser();
      setUpgradeMessage('Your plan has been upgraded successfully!');
      setUpgradeSuccess(true);
      setSearchParams({}, { replace: true });
    } else if (upgrade === 'cancelled') {
      setUpgradeMessage('Upgrade was cancelled. No charge was made.');
      setUpgradeSuccess(false);
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <PageHeader title="Settings" subtitle="Account settings and security" />
      {upgradeMessage && (
        <div className={upgradeSuccess ? styles.upgradeSuccess : styles.upgradeInfo}>
          {upgradeMessage}
        </div>
      )}
      <div className={styles.sections}>
        <ProfileSection user={user} onEdit={() => setEditOpen(true)} />
        <TwoFactorSection user={user} />
        <ChangePasswordSection />
        <NotificationsSection />
      </div>
      {editOpen && (
        <EditProfileModal
          user={user}
          onClose={() => setEditOpen(false)}
          onSaved={fetchUser}
        />
      )}
    </div>
  );
}

export default Settings;
